import { useState } from 'react'
import { ClipboardDocumentIcon, CheckIcon } from '@heroicons/react/24/outline'
import { elidePayloads } from '../utils/command'

/**
 * A generated command, shown short.
 *
 * A bundle one-liner or a published install command is several hundred characters of token and
 * base64. Shown whole it is a wall of text on a card that has other things to say, and it is not
 * read anyway — the copy button is what carries it to the machine. So the resting state is one
 * line, `summary` when the command has nothing readable in it at all, and the whole thing is a
 * click away for anyone who does want to check it. Copy always writes the command verbatim.
 */
export default function CommandBlock({
  command,
  summary,
  copyLabel = 'Copy command',
}: {
  command: string
  /** What the command does, for one whose every character is payload. */
  summary?: string
  copyLabel?: string
}) {
  const [copied, setCopied] = useState(false)
  const [shown, setShown] = useState(false)

  const copy = () => {
    navigator.clipboard?.writeText(command).catch(() => {})
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  return (
    <div className="rounded-lg border border-line bg-base px-3 py-2">
      <div className="flex items-center gap-2">
        {summary ? (
          <span className="min-w-0 flex-1 truncate text-[12px] text-fmuted">{summary}</span>
        ) : (
          // min-w-0 is what lets a flex child shrink below its own text, which is what makes
          // `truncate` cut the command instead of stretching the card.
          <code className="min-w-0 flex-1 truncate font-mono text-[12px] text-ftext">
            {elidePayloads(command)}
          </code>
        )}
        <button
          type="button"
          onClick={() => setShown((s) => !s)}
          aria-expanded={shown}
          className="shrink-0 text-[11px] font-medium text-fmuted hover:text-ftext"
        >
          {shown ? 'Hide' : 'Show'}
        </button>
        <button
          type="button"
          onClick={copy}
          aria-label={copyLabel}
          className="shrink-0 text-fmuted hover:text-ftext"
        >
          {copied ? (
            <CheckIcon className="h-4 w-4 text-emerald-500 dark:text-emerald-400" />
          ) : (
            <ClipboardDocumentIcon className="h-4 w-4" />
          )}
        </button>
      </div>
      {shown && (
        <code className="mt-2 block max-h-40 overflow-y-auto break-all border-t border-line pt-2 font-mono text-[11px] leading-relaxed text-ftext">
          {command}
        </code>
      )}
    </div>
  )
}
